import { useState, useEffect, useCallback } from "preact/compat"
import { IntegrationCardButton } from "./integration-card"
import { NewConnectionCard } from "./new-connection-card"
import { EmailSourceWall } from "./email-source-wall"
import { emailSources, search_email_sources } from "./email_sources"
import {
  allIntegrations,
  distributorIntegrations,
  retailerIntegrations,
  accountingIntegrations,
  logisticsIntegrations,
  dataIntegrations,
  planningIntegrations,
} from "./integrations"
import {
  type ConnectTabValue,
  connect_integration_url,
  connect_target_id,
  default_connect_tab,
  group_by_class_of_trade,
  normalize_connect_list_location,
  parse_connect_tab_from_location,
  resolve_connect_tab,
  search_connect_integrations,
  visible_connect_tabs,
} from "./connect_tab"
import { getIntegrations } from "@/api/integration"
import { getStordConfig } from "@/api/organization"
import { useAsyncEffect, usePageTitle } from "@/utils"
import { Logo } from "@/app/logo"
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import { BackToSettingsButton } from "./settings-connect-layout"
import { Button } from "@/components/ui/button"
import { SetupProgressBar } from "@/setup/SetupProgressBar"
import { useLocation } from "wouter-preact"
import { UserState, is_system_admin } from "@/auth/user"

type TabValue = ConnectTabValue

const EMAIL_REMITTANCE_ID = "email-remittance"

function read_active_tab(admin: boolean): TabValue {
  if (typeof window === "undefined") {
    return default_connect_tab()
  }
  const requested = parse_connect_tab_from_location(window.location.search, window.location.hash)
  return resolve_connect_tab(requested, admin)
}

const TAB_LABELS: Record<TabValue, string> = {
  distributor: "Distributors",
  retailer: "Retailers",
  accounting: "Accounting",
  logistics: "Logistics",
  data: "Sales Data",
  planning: "Planning",
}

function getIntegrationsForTab(tab: TabValue) {
  switch (tab) {
    case "distributor":
      return distributorIntegrations
    case "retailer":
      return retailerIntegrations
    case "accounting":
      return accountingIntegrations
    case "logistics":
      return logisticsIntegrations
    case "data":
      return dataIntegrations
    case "planning":
      return planningIntegrations
  }
}

interface ConnectPageProps {
  baseUrl: string
  setupNextPath?: string
}

export function ConnectPage({ baseUrl, setupNextPath }: ConnectPageProps) {
  const [, setLocation] = useLocation()
  const user = UserState.use()
  const admin = is_system_admin(user)
  const tabs = visible_connect_tabs(admin).map(value => ({ value, label: TAB_LABELS[value] }))
  const [search, setSearch] = useState("")
  const [activeTab, setActiveTab] = useState<TabValue>(() => read_active_tab(admin))
  const [connectedIntegrations, setConnectedIntegrations] = useState<Set<string>>(new Set())
  const [stordConfigured, setStordConfigured] = useState(false)

  const select_tab = useCallback((tab: TabValue) => {
    setActiveTab(tab)
    const normalized = normalize_connect_list_location(
      window.location.pathname,
      window.location.search
    )
    const base = normalized.split("#")[0]
    window.history.replaceState(null, "", `${base}#${tab}`)
  }, [])

  useEffect(() => {
    const normalized = normalize_connect_list_location(
      window.location.pathname,
      window.location.search
    )
    if (normalized !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
      window.history.replaceState(null, "", normalized)
    }
    setActiveTab(read_active_tab(admin))

    function handle_hash_change() {
      setActiveTab(read_active_tab(admin))
    }
    window.addEventListener("hashchange", handle_hash_change)
    return () => window.removeEventListener("hashchange", handle_hash_change)
    // `admin` is a dependency because UserState is persisted and rehydrates after the first render:
    // without it a system admin following a #planning link would land on Distributors.
  }, [admin])

  useAsyncEffect(async () => {
    const [integrationsRes, stordRes] = await Promise.all([getIntegrations(), getStordConfig()])
    if (integrationsRes.ok) {
      const connected = new Set(integrationsRes.value.data.map(i => i.name))
      setConnectedIntegrations(connected)
    }
    if (stordRes.ok) {
      setStordConfigured(stordRes.value.data.configured)
    }
  }, [])

  function isConnected(integrationId: string): boolean {
    if (integrationId === "stord") {
      return stordConfigured
    }
    return connectedIntegrations.has(integrationId)
  }

  const isSearching = search.length > 0
  const filteredIntegrations = search_connect_integrations(allIntegrations, search, admin)
  // The catch-all "Remittance emails" card is replaced on the list by the wall of the customers it
  // covers; the connection itself (and its place in search) is unchanged.
  const tabIntegrations = getIntegrationsForTab(activeTab).filter(config => config.id !== EMAIL_REMITTANCE_ID)
  const matchedEmailSources = search_email_sources(emailSources, search)
  const showEmailWall = !isSearching && (activeTab === "distributor" || activeTab === "retailer")
  const emailConnectHref = connect_integration_url(baseUrl, EMAIL_REMITTANCE_ID, activeTab)
  const groupedDistributors = !isSearching && activeTab === "distributor" ? group_by_class_of_trade(tabIntegrations) : null

  const fromMcp =
    new URLSearchParams(window.location.search).has("from") &&
    new URLSearchParams(window.location.search).get("from") === "mcp"
  const showSetupNext = setupNextPath && !fromMcp

  return (
    <div class="min-h-screen bg-white">
      <div class="max-w-xl mx-auto px-6 pt-24">
        <div class="flex justify-center mb-20">
          <Logo />
        </div>

        {fromMcp && (
          <div class="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            Connect your distributor accounts below. When you're done, head back to Claude and ask about your deductions.
          </div>
        )}

        <div class="mb-10">
          {showSetupNext && <SetupProgressBar progress={50} />}
          <h1 class="text-2xl font-semibold text-gray-900 mb-2">Connect Your Data</h1>
          <p class="text-gray-500">Link your accounts to automatically import deductions</p>
        </div>

        {/* Search */}
        <div class="relative mb-6">
          <MagnifyingGlassIcon class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search integrations..."
            value={search}
            onInput={e => setSearch((e.target as HTMLInputElement).value)}
            class="w-full pl-10 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
          />
        </div>

        {/* Tabs */}
        <div class="h-10 mb-6">
          {!isSearching && (
            <div class="flex border-b border-gray-200">
              {tabs.map(tab => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => select_tab(tab.value)}
                  class={`flex-1 pb-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    activeTab === tab.value
                      ? "border-gray-900 text-gray-900"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}>
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        {/* Distributors read as one long wall of cards, so that tab is sectioned by class of trade.
            Search and the other tabs stay a flat grid. */}
        {groupedDistributors &&
          groupedDistributors.map(group => (
            <div key={group.key} class="mb-8">
              <h2 class="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">{group.label}</h2>
              <div class="grid grid-cols-2 gap-3">
                {group.integrations.map(config => (
                  <IntegrationCardButton
                    key={config.id}
                    config={config}
                    connected={isConnected(connect_target_id(config))}
                    href={connect_integration_url(baseUrl, connect_target_id(config), activeTab)}
                  />
                ))}
              </div>
            </div>
          ))}
        <div class="grid grid-cols-2 gap-3">
          {!groupedDistributors &&
            (isSearching ? filteredIntegrations : tabIntegrations).map(config => (
              <IntegrationCardButton
                key={config.id}
                config={config}
                connected={isConnected(connect_target_id(config))}
                href={connect_integration_url(baseUrl, connect_target_id(config), activeTab)}
              />
            ))}
          <NewConnectionCard next={baseUrl + "/new"} />
          {isSearching && filteredIntegrations.length === 0 && matchedEmailSources.length === 0 && (
            <p class="col-span-2 text-center text-gray-400 py-12 text-sm">No integrations found</p>
          )}
        </div>

        {isSearching && (
          <EmailSourceWall
            compact
            className="mt-8"
            sources={matchedEmailSources}
            href={emailConnectHref}
            connected={isConnected(EMAIL_REMITTANCE_ID)}
          />
        )}
        {showEmailWall && (
          <EmailSourceWall
            className="mt-10"
            sources={emailSources}
            href={emailConnectHref}
            connected={isConnected(EMAIL_REMITTANCE_ID)}
          />
        )}

        {showSetupNext && (
          <div class="mt-8 border-t border-gray-200 pt-6">
            <p class="mb-4 text-sm text-gray-500">
              Once you've connected the accounts you want to start with, continue to invite team members.
            </p>
            <Button className="w-full" onClick={() => setLocation(setupNextPath)}>
              Continue to Add Team Members
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export function SetupConnectPage() {
  return <ConnectPage baseUrl="/setup/connect" setupNextPath="/setup/invites" />
}

export function SettingsConnectPage() {
  usePageTitle("Connect")
  return (
    <div class="relative">
      <div class="absolute top-6 left-6 z-10">
        <BackToSettingsButton />
      </div>
      <ConnectPage baseUrl="/settings/connect" />
    </div>
  )
}
